import makeStyles from '@mui/styles/makeStyles';
import { Grid, Theme, Typography, Box, Divider, List, ListItem, Chip } from '@mui/material';
import { OrderManagementPage } from '../../../../components/skeleton/OrderManagementPage';
import { Link, Loader, PageTitle, Pagination, SearchField, SearchSection } from '../../../../components';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getPunchlistSearchResults } from '../../../../fetch';
import { IPunchlistSearchResult, ItemType, ItemTypeValue } from '../../../../models';
import { AssignmentTurnedIn, AttachFile, ChatBubble } from '@mui/icons-material';
import { formatDate } from '../../../../helpers';
import { ExternalLink } from '../../../../components/link/ExternalLink';

export const PunchlistSearch = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [hasPageLoaded, setHasPageLoaded] = useState<boolean>(false); // Need this to prevent fetch from rendering on page load
  const [isLoadingSearchResults, setLoadingSearchResults] = useState<boolean>(false);
  const [punchlistSearchResults, setPunchlistSearchResults] = useState<IPunchlistSearchResult[]>([]);

  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);

  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const fetchPunchlistSearchResults = async () => {
    setLoadingSearchResults(true);
    try {
      const res = await getPunchlistSearchResults({
        page: page + 1,
        perPage,
        sortBy: 'CreatedDate',
        sortDirection: 'Desc',
        search: searchedValue
      });
      setPunchlistSearchResults(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(`Error loading search results, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingSearchResults(false);
    }
  };

  const getTypeChip = (type: ItemTypeValue) => {
    let text = theme => theme.palette.primary.contrastText;
    let icon = <AssignmentTurnedIn sx={{ color: text }} />;
    let color = theme => theme.palette.primary.dark;
    let label = 'To-Do';

    if (type === ItemType.COMMENT) {
      icon = <ChatBubble sx={{ color: text }} />;
      color = theme => theme.palette.primary.main;
      label = 'Comment';
    }

    if (type === ItemType.FILE_UPLOAD) {
      text = theme => theme.palette.tertiary.contrastText;
      icon = <AttachFile sx={{ color: text }} />;
      color = theme => theme.palette.tertiary.main;

      label = 'File Upload';
    }

    return (
      <Chip
        icon={icon}
        className={classes.resultChip}
        sx={{
          backgroundColor: color,
          color: text
        }}
        label={label}
      />
    );
  };

  const handleClearSearch = () => {
    setHasPageLoaded(false);
    setSearchValue('');
    setSearchedValue('');
    setPage(0);
    setPunchlistSearchResults([]);
  };

  useEffect(() => {
    if (hasPageLoaded) {
      fetchPunchlistSearchResults();
    } else {
      setHasPageLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, searchedValue]);

  return (
    <OrderManagementPage title="Punchlist Search">
      <PageTitle title="Punchlist Search" />
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12} lg={8} xl={6}>
          <SearchField
            isDisabled={isLoadingSearchResults}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
            }}
            handleClearSearch={() => handleClearSearch()}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Grid>
      </Grid>
      <Divider sx={{ margin: theme => theme.spacing(1, 0) }} />
      {searchedValue && <SearchSection searchedValue={searchedValue} handleClearSearch={() => handleClearSearch()} />}
      {isLoadingSearchResults && <Loader position="centered" type="inline" />}
      {!isLoadingSearchResults && searchedValue && (punchlistSearchResults?.length === 0 || !punchlistSearchResults) && (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ backgroundColor: theme => theme.palette.grey[100] }} marginTop="1rem" minHeight="10rem">
          <Typography variant="body1" gutterBottom>
            There are no results that match the search term(s). Please try again with some different keywords.
          </Typography>
        </Box>
      )}

      {!isLoadingSearchResults && searchedValue && punchlistSearchResults && punchlistSearchResults?.length > 0 && (
        <>
          <List>
            {punchlistSearchResults.map((result, resultIndex) => {
              const author = !!result.createdBy ? `${result.createdBy}: ` : '';
              const territory = !!result.territory ? `(${result.territory}) - ` : '';
              const neighborhood = !!result.neighborhood ? result.neighborhood : '';
              return (
                <ListItem key={resultIndex} disablePadding className={classes.result}>
                  <Typography component="h4" variant="h5" className={classes.resultTitle}>
                    {getTypeChip(result.itemType)} <Link to={`/production/punchlists/${result.punchListId}`}>{result.punchListName}</Link>
                  </Typography>
                  <Typography component="p" variant="body2" className={classes.resultSubTitle}>
                    <strong>
                      {author}
                      {territory}
                      {neighborhood}
                    </strong>
                  </Typography>
                  {!!result.punchListItemDescription && (
                    <Typography component="p" variant="body1" className={classes.resultPreview}>
                      {result.itemType === ItemType.FILE_UPLOAD ? (
                        <ExternalLink to={result.punchListItemDescription} target="_blank">
                          {result.fileName}
                        </ExternalLink>
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: result.punchListItemDescription }} />
                      )}
                    </Typography>
                  )}
                  {!!result.createdOn && (
                    <Typography component="p" variant="body2" className={classes.resultDate}>
                      {formatDate(result.createdOn)}
                    </Typography>
                  )}
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      {!isLoadingSearchResults && searchedValue && punchlistSearchResults?.length > 0 && (
        <Pagination page={page} count={recordCount} rowsPerPage={perPage} setPage={setPage} setRowsPerPage={setRowsPerPage} />
      )}
    </OrderManagementPage>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  result: {
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    display: 'block',
    '&&:first-child': {
      paddingTop: 0,
      borderTop: 'none'
    }
  },
  resultTitle: { marginBottom: theme.spacing(0.25) },
  resultSubTitle: {},
  resultPreview: {
    margin: theme.spacing(0.4, 0),
    [theme.breakpoints.up('md')]: {
      maxWidth: '85%'
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '75%'
    }
  },
  resultDate: { color: theme.palette.grey[600] },
  resultChip: {
    height: 'auto',
    padding: theme.spacing(0.4, 0.2),
    lineHeight: 1,
    fontWeight: 'bold',
    marginRight: theme.spacing(0.25),
    '&& .MuiChip-icon': {
      color: 'inherit !important',
      fontSize: theme.spacing(1)
    }
  }
}));
